import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Heart, ShoppingCart, X } from 'lucide-react';
import type { ProArticle, ProArticlePricing } from '@erp/domain/pro';
import {
  calculateLineCommission,
  calculateProLineTotals,
  resolveTierLinePricing,
} from '@erp/domain/pro';
import { formatTND } from '@/lib/format';
import { useCart } from '@/lib/cart-context';
import { useFlyToCart } from '@/lib/fly-to-cart';
import { useProPrefs } from '@/lib/user-prefs';
import { useProPublishedCategories } from '@/hooks/use-pro-articles';
import { useClientMode } from '@/lib/client-mode';
import { QuantityStepper } from './QuantityStepper';

export interface ProductDetailModalProps {
  article: ProArticle | null;
  pricing: ProArticlePricing | null;
  /** Pas de prop `tier` : le prix affiché dérive de `useCart().tier`, donc
   *  c'est exactement celui qu'`addItem` enregistrera. Un palier passé de
   *  l'extérieur rouvrirait la porte à un prix de fiche ≠ prix du panier. */
  isFavorite: boolean;
  onToggleFavorite: () => void;
  onClose: () => void;
  onAddToCart: (quantity: number) => void;
}

// `article.brandId` is a loose column (name for legacy rows, UUID for new
// ones) — a raw UUID is meaningless to the electrician, so hide it.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const LONG_DESCRIPTION_CHARS = 200;

export function ProductDetailModal({
  article,
  pricing,
  isFavorite,
  onToggleFavorite,
  onClose,
  onAddToCart,
}: ProductDetailModalProps) {
  const { t } = useTranslation();
  const { tier: cartTier, items, commissionRate } = useCart();
  const { showCommission } = useProPrefs();
  const clientMode = useClientMode();
  const { flyToCart } = useFlyToCart();
  const proCategoriesQuery = useProPublishedCategories();

  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [descOpen, setDescOpen] = useState(false);
  const [favoriteBump, setFavoriteBump] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);

  const articleId = article?.id ?? null;
  const open = article !== null;

  // The component stays mounted across products (article|null prop) — reset
  // the per-product state whenever the displayed article changes.
  useEffect(() => {
    setQuantity(1);
    setActiveImage(0);
    setDescOpen(false);
    setFavoriteBump(0);
  }, [articleId]);

  // Dialog behaviour while open: Escape closes, Tab loops inside the panel
  // (aria-modal without a trap lets Tab escape into the scroll-locked page),
  // body scroll is locked.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const panel = panelRef.current;
        if (!panel) return;
        const focusables = panel.querySelectorAll<HTMLElement>(
          'button, input, [href], select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        const active = document.activeElement;
        if (e.shiftKey && (active === first || active === panel)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  // Move focus into the dialog on open and give it BACK to the trigger on
  // close (separate from the listener effect so a re-render never steals
  // focus back from the quantity field).
  useEffect(() => {
    if (!open) return;
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panelRef.current?.focus();
    return () => { trigger?.focus(); };
  }, [open]);

  const categoryName = useMemo(() => {
    if (!article) return null;
    const byId = new Map<string, string | null>();
    for (const c of proCategoriesQuery.data ?? []) {
      byId.set(c.articleCategoryId, c.nameFr ?? c.articleCategoryName ?? null);
    }
    return (
      (article.subFamily ? byId.get(article.subFamily) : null) ??
      (article.categoryId ? byId.get(article.categoryId) : null) ??
      null
    );
  }, [article, proCategoriesQuery.data]);

  if (!article) return null;

  const images =
    article.images && article.images.length > 0
      ? article.images
      : article.imageUrl
        ? [article.imageUrl]
        : [];
  const mainImage = images[activeImage] ?? images[0] ?? null;

  const brandLabel =
    article.brandId && !UUID_RE.test(article.brandId) ? article.brandId : null;
  const specChips = [article.unit, brandLabel, categoryName].filter(
    (s): s is string => Boolean(s && s.trim()),
  );

  // Everything priced at the CART tier — the tier addItem will snapshot.
  const linePricing = resolveTierLinePricing(article, pricing, cartTier);
  const unitTotals = calculateProLineTotals({
    quantity: 1,
    unitHt: linePricing.unitHt,
    remisePct: linePricing.remisePct,
    vatRate: article.vatRate,
  });
  // Prix public barré + pastille « −x% » : c'est la remise de l'ÉLECTRICIEN.
  // En mode client on ne l'annonce pas — le prix affiché reste le prix remisé,
  // il se présente simplement comme LE prix. Neutraliser `baseUnitTtc` suffit :
  // le barré et la pastille sont tous deux conditionnés par lui.
  const baseUnitTtc =
    !clientMode && linePricing.remisePct > 0
      ? calculateProLineTotals({
          quantity: 1,
          unitHt: article.salePriceHt,
          remisePct: 0,
          vatRate: article.vatRate,
        }).pttc
      : null;
  const lineTotals = calculateProLineTotals({
    quantity,
    unitHt: linePricing.unitHt,
    remisePct: linePricing.remisePct,
    vatRate: article.vatRate,
  });

  const inCartQty =
    items.find((it) => it.articleId === article.id)?.quantity ?? 0;

  const commissionVisible = !clientMode && showCommission && commissionRate > 0;
  const lineCommission = commissionVisible
    ? calculateLineCommission(
        {
          quantity,
          unitHt: linePricing.unitHt,
          remisePct: linePricing.remisePct,
          purchasePrice: article.purchasePrice,
        },
        commissionRate,
      )
    : 0;

  const isLongDescription =
    (article.description?.length ?? 0) > LONG_DESCRIPTION_CHARS;

  const handleFavorite = () => {
    setFavoriteBump((k) => k + 1);
    onToggleFavorite();
  };

  // Launch the fly animation from the modal image BEFORE the add handler
  // closes the modal (the clone captures the source rect synchronously).
  const handleAdd = () => {
    if (imageRef.current) {
      flyToCart(imageRef.current, mainImage);
    }
    onAddToCart(quantity);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/40 pro-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pro-product-detail-name"
        tabIndex={-1}
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col pro-scale-in focus:outline-none"
      >
        <div className="h-14 px-5 border-b border-neutral-200 flex items-center justify-between gap-3 shrink-0">
          <span className="font-mono text-xs text-neutral-500 truncate">
            {article.code}
          </span>
          <div className="flex items-center gap-1.5 shrink-0">
            {!clientMode && (
              <button
                type="button"
                aria-label={
                  isFavorite
                    ? t('pro.product.favorite_remove', 'Retirer des favoris')
                    : t('pro.product.favorite_add', 'Ajouter aux favoris')
                }
                aria-pressed={isFavorite}
                onClick={handleFavorite}
                className={`p-2 rounded-lg border ${
                  isFavorite
                    ? 'border-red-200 bg-red-50 text-red-500'
                    : 'border-neutral-200 text-neutral-400 hover:text-red-500'
                }`}
              >
                <Heart
                  key={favoriteBump}
                  className={`w-4 h-4 ${favoriteBump ? 'pro-pop' : ''}`}
                  fill={isFavorite ? 'currentColor' : 'none'}
                />
              </button>
            )}
            <button
              type="button"
              aria-label={t('pro.product.close', 'Fermer')}
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-neutral-100 text-neutral-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Gallery */}
          <div>
            <div
              ref={imageRef}
              className="aspect-square rounded-xl bg-neutral-50 flex items-center justify-center overflow-hidden"
            >
              {mainImage ? (
                <img
                  src={mainImage}
                  alt={article.name}
                  className="w-full h-full object-contain p-4"
                />
              ) : (
                <span className="text-6xl text-neutral-300">⚡</span>
              )}
            </div>
            {images.length > 1 && (
              <div className="mt-3 flex gap-2 overflow-x-auto pro-noscrollbar">
                {images.map((src, i) => {
                  const active = i === activeImage;
                  return (
                    <button
                      key={`${src}-${i}`}
                      type="button"
                      aria-label={t('pro.product.view_image', 'Image {{index}}', {
                        index: i + 1,
                      })}
                      aria-pressed={active}
                      onClick={() => setActiveImage(i)}
                      className={`size-14 shrink-0 rounded-lg bg-neutral-50 overflow-hidden border ${
                        active ? '' : 'border-neutral-200 hover:border-neutral-300'
                      }`}
                      style={
                        active
                          ? {
                              borderColor: 'var(--pro-primary)',
                              boxShadow: '0 0 0 1px var(--pro-primary)',
                            }
                          : undefined
                      }
                    >
                      <img
                        src={src}
                        alt=""
                        className="w-full h-full object-contain p-1"
                        loading="lazy"
                      />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="flex flex-col">
            <h2
              id="pro-product-detail-name"
              className="text-xl font-semibold text-neutral-900"
            >
              {article.name}
            </h2>
            {specChips.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {specChips.map((chip, i) => (
                  <span
                    key={`${chip}-${i}`}
                    className="inline-flex items-center h-6 px-2 rounded-full bg-neutral-100 text-[11px] font-medium text-neutral-600"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            )}

            {/* Price panel — cart-tier price = the price addItem snapshots */}
            <div className="mt-4 rounded-xl bg-neutral-50 p-4">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-2xl font-bold text-neutral-900">
                  {formatTND(unitTotals.pttc)}
                </span>
                <span className="text-xs font-semibold text-neutral-500">
                  {t('pro.product.ttc', 'TTC')}
                </span>
                {baseUnitTtc != null && (
                  <>
                    <span className="text-sm text-neutral-400 line-through">
                      {formatTND(baseUnitTtc)}
                    </span>
                    <span
                      className="inline-flex items-center h-5 px-1.5 rounded-md text-[11px] font-bold"
                      style={{
                        background: 'var(--pro-primary-subtle)',
                        color: 'var(--pro-primary-text)',
                      }}
                    >
                      −{linePricing.remisePct}%
                    </span>
                  </>
                )}
              </div>
              <div className="text-[13px] text-neutral-500 mt-1">
                {t('pro.product.ht_line', '{{amount}} HT · TVA {{rate}}%', {
                  amount: formatTND(unitTotals.pht),
                  rate: article.vatRate,
                })}
              </div>
            </div>

            {/* Description — accordion only when it's long */}
            {article.description &&
              (isLongDescription ? (
                <div className="mt-4 rounded-xl border border-neutral-200">
                  <button
                    type="button"
                    aria-expanded={descOpen}
                    onClick={() => setDescOpen((o) => !o)}
                    className="w-full h-11 px-4 flex items-center justify-between text-sm font-medium text-neutral-900"
                  >
                    {t('pro.product.description', 'Description')}
                    <ChevronDown
                      className="w-4 h-4 text-neutral-400 pro-chevron-rotate"
                      data-open={descOpen ? 'true' : 'false'}
                    />
                  </button>
                  <div
                    className="pro-accordion"
                    data-open={descOpen ? 'true' : 'false'}
                  >
                    <div className="pro-accordion-content">
                      <p className="px-4 pb-4 text-sm text-neutral-600 leading-relaxed">
                        {article.description}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="mt-4 text-sm text-neutral-600 leading-relaxed">
                  {article.description}
                </p>
              ))}

            {/* Quantity + live line total */}
            <div className="mt-4 flex items-end justify-between gap-4">
              <div>
                <QuantityStepper
                  value={quantity}
                  onChange={(n) => setQuantity(Math.max(1, n))}
                  decreaseLabel={t('pro.product.decrease', 'Diminuer')}
                  increaseLabel={t('pro.product.increase', 'Augmenter')}
                  inputLabel={t('pro.product.quantity', 'Quantité')}
                />
                {inCartQty > 0 && (
                  <div className="mt-1.5 text-[11px] text-neutral-500">
                    {t('pro.product.in_cart', 'Déjà {{count}} au panier', {
                      count: inCartQty,
                    })}
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="text-[11px] text-neutral-500">
                  {t('pro.product.total', 'Total')}
                </div>
                <div className="flex items-baseline justify-end gap-1">
                  <span className="text-lg font-bold text-neutral-900">
                    {formatTND(lineTotals.pttc)}
                  </span>
                  <span className="text-[10px] font-semibold text-neutral-500">
                    {t('pro.product.ttc', 'TTC')}
                  </span>
                </div>
                <div className="text-[11px] text-neutral-500">
                  {t('pro.product.ht_short', '{{amount}} HT', {
                    amount: formatTND(lineTotals.pht),
                  })}
                </div>
              </div>
            </div>

            {commissionVisible && (
              <div className="mt-4 pt-3 border-t border-neutral-200 flex items-center justify-between text-[12px]">
                <span className="font-medium text-neutral-500">
                  {t('pro.product.commission', 'Votre commission')}
                </span>
                <span
                  className="font-semibold"
                  style={{ color: 'var(--pro-primary-text)' }}
                >
                  {formatTND(lineCommission)}
                </span>
              </div>
            )}

            <button
              type="button"
              onClick={handleAdd}
              className="mt-4 w-full h-12 rounded-xl text-[15px] font-semibold text-white inline-flex items-center justify-center gap-2 pro-primary-bg"
            >
              <ShoppingCart className="w-4 h-4" />
              {t('pro.product.add_to_cart', 'Ajouter au panier')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
